type ApiBody = BodyInit | Record<string, unknown> | null | undefined;
let smsCsrfRequest: Promise<string> | null = null;

async function smsCsrfToken(): Promise<string> {
  const existing = getCookie('csrftoken');
  if (existing) return existing;
  if (!smsCsrfRequest) {
    smsCsrfRequest = fetch('/api/auth/sms/send/', { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } })
      .then(async (response) => {
        if (!response.ok) throw new ApiError('Could not initialize SMS verification.', response.status, null);
        const payload = await response.json() as { csrfToken?: string };
        return getCookie('csrftoken') || payload.csrfToken || '';
      }).finally(() => { smsCsrfRequest = null; });
  }
  return smsCsrfRequest;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly payload: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export function getCookie(name: string): string {
  const prefix = `${encodeURIComponent(name)}=`;
  const cookie = document.cookie
    .split(';')
    .map((value) => value.trim())
    .find((value) => value.startsWith(prefix));
  return cookie ? decodeURIComponent(cookie.slice(prefix.length)) : '';
}

export function uploadFormData<T>(
  path: string,
  body: FormData,
  onProgress?: (percent: number) => void,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('POST', path);
    request.responseType = 'json';
    request.withCredentials = true;
    request.setRequestHeader('Accept', 'application/json');
    const csrfToken = getCookie('csrftoken');
    if (csrfToken) request.setRequestHeader('X-CSRFToken', csrfToken);
    request.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) onProgress?.(Math.round((event.loaded / event.total) * 100));
    });
    request.addEventListener('load', () => {
      const payload = request.response ?? request.responseText;
      if (request.status >= 200 && request.status < 300) {
        onProgress?.(100);
        resolve(payload as T);
        return;
      }
      const detail = payload && typeof payload === 'object' && 'detail' in payload
        ? String((payload as { detail: unknown }).detail)
        : `HTTP ${request.status}`;
      reject(new ApiError(detail, request.status, payload));
    });
    request.addEventListener('error', () => reject(new ApiError('Upload failed', 0, null)));
    request.addEventListener('abort', () => reject(new ApiError('Upload cancelled', 0, null)));
    request.send(body);
  });
}

function isBodyInit(body: ApiBody): body is BodyInit {
  return (
    typeof body === 'string' ||
    body instanceof Blob ||
    body instanceof FormData ||
    body instanceof URLSearchParams ||
    body instanceof ArrayBuffer ||
    ArrayBuffer.isView(body)
  );
}

export async function api<T>(
  path: string,
  init: Omit<RequestInit, 'body'> & { body?: ApiBody } = {},
): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase();
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');

  let body: BodyInit | null | undefined;
  if (isBodyInit(init.body)) body = init.body;
  else if (init.body != null) {
    headers.set('Content-Type', 'application/json');
    body = JSON.stringify(init.body);
  }

  if (!['GET', 'HEAD', 'OPTIONS', 'TRACE'].includes(method)) {
    const csrfToken = path === '/api/auth/sms/send/' ? await smsCsrfToken() : getCookie('csrftoken');
    if (csrfToken) headers.set('X-CSRFToken', csrfToken);
  }

  const response = await fetch(path, {
    ...init,
    method,
    headers,
    body,
    cache: 'no-store',
    credentials: 'same-origin',
  });
  const contentType = response.headers.get('content-type') ?? '';
  const payload = contentType.includes('application/json')
    ? await response.json()
    : await response.text();

  if (!response.ok) {
    let message = `HTTP ${response.status}`;
    if (typeof payload === 'object' && payload !== null) {
      if ('detail' in payload) {
        message = String((payload as { detail: unknown }).detail);
      } else {
        const values = Object.values(payload);
        if (values.length > 0) {
          const first = values[0];
          if (Array.isArray(first) && first.length > 0) {
            message = String(first[0]);
          } else if (typeof first === 'string') {
            message = first;
          }
        }
      }
    }
    throw new ApiError(message, response.status, payload);
  }
  return payload as T;
}

export async function apiList<T>(path: string, init?: RequestInit): Promise<T[]> {
  const payload = await api<T[] | { results: T[] }>(path, init);
  return Array.isArray(payload) ? payload : payload.results;
}
